import { get } from 'lodash';
import React, { useContext, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ActionButtonHandle,
  DetailContext,
  DetailHandle,
  DetailMode,
  FormContext,
  SnackbarContext,
  SnackbarVariant,
  abortableFetch,
  useCrudSource,
  useEventCallback,
  useFormSelector,
  useScrollableSource,
} from '@eas/common-web';
import { HistoryField } from '@composite/history/history-field';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { Contact, RawSimpleLog, Subject } from '@models';
import { EvidenceApiUrl, HistoryEntityType } from '@enums';
import { useAddSubjectDialog } from './dialog-actions/add-subject-hook';
import { useEditSubjectDialog } from './dialog-actions/edit-subject-hook';
import { Fields } from './subject-fields';
import { SubjectHistoryContext } from './subject-history-context';
import { useSubjectToolbarValidationSchema } from './subject-schema';
import { resolveSelectedContact } from './subject-toolbar-utils';

export const SubjectPreselectContactContext = React.createContext<
  string | null | undefined
>(null);

export function SubjectToolbar({
  createRef,
  editRef,
  fieldName = 'subject',
  onResultValue,
  participantMode = false,
  preselectContactId,
}: {
  createRef: React.RefObject<ActionButtonHandle>;
  editRef: React.RefObject<ActionButtonHandle>;
  fieldName?: string;
  onResultValue?: (result: Subject) => void;
  participantMode?: boolean;
  preselectContactId?: string;
}) {
  const intl = useIntl();
  const selectedContactRef = useRef<Contact | null>(null);

  const { showSnackbar } = useContext(SnackbarContext);
  const { setFieldValue } = useContext(FormContext);

  const { subject } = useFormSelector((values: Subject) => ({
    subject: get(values, fieldName),
  }));

  const createValidationSchema = useSubjectToolbarValidationSchema({
    participantMode,
    mode: DetailMode.NEW,
  });
  const editValidationSchema = useSubjectToolbarValidationSchema({
    participantMode,
    mode: DetailMode.EDIT,
  });

  const { callApi: addSubjectApiCall } = useAddSubjectDialog();
  const { callApi: editSubjectApiCall } = useEditSubjectDialog();

  const source = useCrudSource<Subject>({
    url: EvidenceApiUrl.SUBJECTS,
  });

  const resetSelectedContact = useEventCallback(() => {
    selectedContactRef.current = null;
  });

  useEffect(() => {
    resetSelectedContact();
    source.reset(subject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject?.id]);

  const handleProcessResult = useEventCallback(
    async (value: Subject, mode: DetailMode) => {
      const explicitlySelectedContact = value.contacts?.find(
        (contact) => contact.id === selectedContactRef.current?.id
      );
      const selectedContact = participantMode
        ? resolveSelectedContact(value, selectedContactRef.current?.id) ??
          undefined
        : explicitlySelectedContact;

      if (participantMode && !selectedContact) {
        if (mode === DetailMode.NEW) {
          resetSelectedContact();
        }

        showSnackbar(
          'Kontakt vytvořeného adresáta nelze jednoznačně určit.',
          SnackbarVariant.ERROR
        );
        return;
      }

      const subjectWithSelectedContact = {
        ...value,
        selectedContact,
      };

      onResultValue
        ? onResultValue(subjectWithSelectedContact)
        : setFieldValue(fieldName, subjectWithSelectedContact);

      const message = intl.formatMessage({
        id: 'EAS_ACTION_BUTTON_MSG_SUCCESS',
        defaultMessage: 'Akce byla úspěšně vykonána.',
      });

      showSnackbar(message, SnackbarVariant.SUCCESS);

      if (mode === DetailMode.NEW) {
        resetSelectedContact();
      }
    }
  );

  const handleSelectRow = useEventCallback(
    (row) => (selectedContactRef.current = row ?? null)
  );

  return (
    <>
      <DetailActionButton
        ref={createRef}
        promptKey="ADD_SUBJECT"
        apiCall={addSubjectApiCall}
        buttonLabel={intl.formatMessage({
          id: 'ES__SUBJECTS_TOOLBAR__ADD_SUBJECT',
          defaultMessage: 'Přidat adresáta',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__SUBJECTS_TOOLBAR__ADD_SUBJECT__TITLE',
          defaultMessage: 'Adresát',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__SUBJECTS_TOOLBAR__ADD_SUBJECT__TEXT',
          defaultMessage: 'Vyplňte informace o adresátovi.',
        })}
        FormFields={() => (
          <DetailContext.Provider
            value={
              {
                source,
                mode: DetailMode.NEW,
                isExisting: !!source?.data?.id,
              } as DetailHandle<Subject>
            }
          >
            <CreateFields
              handleSelectRow={handleSelectRow}
              resetSelectedContact={resetSelectedContact}
            />
          </DetailContext.Provider>
        )}
        modes={[]}
        refreshAfterConfirm={false}
        dialogWidth={1000}
        formValidationSchema={createValidationSchema}
        handleBeforeDialogOpen={resetSelectedContact}
        closeCallback={resetSelectedContact}
        onResult={(value) => handleProcessResult(value, DetailMode.NEW)}
      />
      <DetailActionButton
        ref={editRef}
        onBeforeDialog={async () => {
          const fetch = abortableFetch(
            `${EvidenceApiUrl.SUBJECTS}/${subject.id}`,
            {
              method: 'GET',
              headers: new Headers({ 'Content-Type': 'application/json' }),
            }
          );

          const subjectDetail = await fetch.json();

          setFieldValue(fieldName, subjectDetail);
          source.reset(subjectDetail);

          return true;
        }}
        promptKey="EDIT_SUBJECT"
        apiCall={(id, values) => editSubjectApiCall(subject.id, values)}
        buttonLabel={intl.formatMessage({
          id: 'ES__SUBJECTS_TOOLBAR__EDIT_SUBJECT',
          defaultMessage: 'Editovat adresáta',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__SUBJECTS_TOOLBAR__EDIT_SUBJECT__TITLE',
          defaultMessage: 'Adresát',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__SUBJECTS_TOOLBAR__EDIT_SUBJECT__TEXT',
          defaultMessage: 'Upravte informace o adresátovi.',
        })}
        FormFields={() => (
          <SubjectPreselectContactContext.Provider value={preselectContactId}>
            <DetailContext.Provider
              value={
                {
                  source,
                  mode: DetailMode.EDIT,
                  isExisting: !!source?.data?.id,
                } as DetailHandle<Subject>
              }
            >
              <FieldsWithHistory handleSelectRow={handleSelectRow} />
            </DetailContext.Provider>
          </SubjectPreselectContactContext.Provider>
        )}
        modes={[]}
        dialogWidth={1000}
        refreshAfterConfirm={false}
        formInitialValues={subject}
        formValidationSchema={editValidationSchema}
        onResult={(value) => handleProcessResult(value, DetailMode.EDIT)}
      />
    </>
  );
}

const CreateFields = ({
  handleSelectRow,
  resetSelectedContact,
}: {
  handleSelectRow: (row?: Contact | null) => void;
  resetSelectedContact: () => void;
}) => {
  const { setFieldValue } = useContext(FormContext);

  useEffect(() => {
    setFieldValue('selectedContact', null);

    return () => {
      setFieldValue('selectedContact', null);
      resetSelectedContact();
    };
    // Reset is intentionally tied to the create form lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fields
      display="OUT_OF_SUBJECT"
      contactsProps={{
        onRowSelect: handleSelectRow,
      }}
      panelLabel={
        <FormattedMessage
          id="ES__SUBJECTS__DETAIL__PANEL_TITLE__RECIPIENT"
          defaultMessage="Adresát"
        />
      }
    />
  );
};

const FieldsWithHistory = ({
  handleSelectRow,
}: {
  handleSelectRow: (row?: Contact | null) => void;
}) => {
  const { source } = useContext(DetailContext);

  const historySource = useScrollableSource<RawSimpleLog>({
    url: `${EvidenceApiUrl.HISTORY}?id=${source.data.id}&entityType=${HistoryEntityType.SUBJECT}`,
    params: {
      size: 10,
      sort: [
        {
          type: 'FIELD',
          field: 'created',
          order: 'DESC',
        },
      ],
    },
  });

  useEffect(() => {
    historySource.reset();
    historySource.loadMore();
  }, []);

  return (
    <SubjectHistoryContext.Provider value={{ historySource }}>
      <Fields
        display="OUT_OF_SUBJECT"
        contactsProps={{
          onRowSelect: handleSelectRow,
        }}
        HistoryFields={() => (
          <HistoryField descriptors={[]} source={historySource} />
        )}
        panelLabel={
          <FormattedMessage
            id="ES__SUBJECTS__DETAIL__PANEL_TITLE__RECIPIENT"
            defaultMessage="Adresát"
          />
        }
      />
    </SubjectHistoryContext.Provider>
  );
};
